import { lazy, StrictMode, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, Link, Navigate, RouterProvider } from "react-router";
import "./index.css";
import { AuthProvider, homeFor, useAuth } from "./lib/auth";
import type { Role } from "./lib/types";
import { AppShell } from "./components/AppShell";
import { Button, Empty, Loading, ToastProvider } from "./components/ui";
import { LoginPage, NoProfile, WelcomePage } from "./pages/Auth";
import { OnboardingPage } from "./pages/student/Onboarding";

// Carga diferida por sección: el editor y las gráficas no se descargan hasta que se usan.
// Después de un despliegue, una pestaña abierta pide archivos de la versión anterior que ya no
// existen ("Failed to fetch dynamically imported module"). En ese caso se recarga la página una
// vez para tomar la versión nueva; si vuelve a fallar en menos de un minuto, se muestra el error.
const RELOAD_KEY = "pei-chunk-reload";
function loadOrReload<T>(loader: () => Promise<T>): Promise<T> {
  return loader().catch((e: unknown) => {
    const msg = String((e as Error)?.message ?? e);
    const isChunk = /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i.test(msg);
    let last = 0;
    try { last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0); } catch { /* sin almacenamiento */ }
    if (isChunk && Date.now() - last > 60_000) {
      try { sessionStorage.setItem(RELOAD_KEY, String(Date.now())); } catch { /* sin almacenamiento */ }
      window.location.reload();
      return new Promise<T>(() => {});
    }
    throw e;
  });
}
const named = <K extends string>(loader: () => Promise<Record<K, React.ComponentType>>, name: K) =>
  lazy(() => loadOrReload(loader).then((m) => ({ default: m[name] })));
// Vite avisa cuando falla la precarga de un archivo; también se resuelve recargando.
window.addEventListener("vite:preloadError", (event) => {
  let last = 0;
  try { last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0); } catch { /* sin almacenamiento */ }
  if (Date.now() - last > 60_000) {
    event.preventDefault();
    try { sessionStorage.setItem(RELOAD_KEY, String(Date.now())); } catch { /* sin almacenamiento */ }
    window.location.reload();
  }
});
const StudentHome = named(() => import("./pages/student/Home"), "StudentHome");
const Workspace = named(() => import("./pages/student/Workspace"), "Workspace");
const VersionsPage = named(() => import("./pages/student/Versions"), "VersionsPage");
const ReviewView = named(() => import("./pages/student/ReviewView"), "ReviewView");
const AnalysesPage = named(() => import("./pages/student/Analyses"), "AnalysesPage");
const AnalysisDetail = named(() => import("./pages/student/Analyses"), "AnalysisDetail");
const StudentSettings = named(() => import("./pages/student/Settings"), "StudentSettings");
const TeacherHome = named(() => import("./pages/teacher/Teacher"), "TeacherHome");
const TeacherGroups = named(() => import("./pages/teacher/Teacher"), "TeacherGroups");
const TeacherClasses = named(() => import("./pages/teacher/Teacher"), "TeacherClasses");
const ClassDetailPage = named(() => import("./pages/teacher/ClassDetail"), "ClassDetailPage");
const SessionsPage = named(() => import("./pages/student/Sessions"), "SessionsPage");
const SessionDetailPage = named(() => import("./pages/student/Sessions"), "SessionDetailPage");
const TeacherReview = named(() => import("./pages/teacher/TeacherReview"), "TeacherReview");
const CoordDashboard = named(() => import("./pages/coord/Dashboard"), "CoordDashboard");
const CohortsPage = named(() => import("./pages/coord/Admin"), "CohortsPage");
const PeoplePage = named(() => import("./pages/coord/Admin"), "PeoplePage");
const LeadsPage = named(() => import("./pages/coord/Admin"), "LeadsPage");
const OrgSettings = named(() => import("./pages/coord/Admin"), "OrgSettings");
const EditorialPlaceholder = named(() => import("./pages/coord/Admin"), "EditorialPlaceholder");

function RequireAuth({ children }: { children: ReactNode }) {
  const { loading, session, profile } = useAuth();
  if (loading) return <div className="mx-auto max-w-[1200px] px-24"><Loading /></div>;
  if (!session) return <Navigate to="/entrar" replace />;
  if (!profile) return <NoProfile />;
  if (!profile.onboarding_completed) return <Navigate to={profile.role === "alumno" ? "/inicio-de-obra" : "/bienvenida"} replace />;
  return <>{children}</>;
}

function Only({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { profile } = useAuth();
  if (!profile || !roles.includes(profile.role)) return <Navigate to={homeFor(profile?.role)} replace />;
  return <>{children}</>;
}

function Index() {
  const { profile } = useAuth();
  if (profile?.role === "alumno") return <StudentHome />;
  return <Navigate to={homeFor(profile?.role)} replace />;
}

function CoordHome() {
  const { profile } = useAuth();
  return profile?.role === "editorial" ? <EditorialPlaceholder /> : <CoordDashboard />;
}

function OnboardingGate() {
  const { loading, session, profile } = useAuth();
  if (loading) return null;
  if (!session) return <Navigate to="/entrar" replace />;
  if (!profile) return <NoProfile />;
  if (profile.role !== "alumno") return <Navigate to={homeFor(profile.role)} replace />;
  return <OnboardingPage />;
}

const S: Role[] = ["alumno"];
const T: Role[] = ["profesor", "coordinacion"];
const C: Role[] = ["coordinacion", "editorial"];

const router = createBrowserRouter([
  { path: "/entrar", element: <LoginPage /> },
  { path: "/bienvenida", element: <WelcomePage /> },
  { path: "/inicio-de-obra", element: <OnboardingGate /> },
  {
    path: "/",
    element: <RequireAuth><AppShell /></RequireAuth>,
    children: [
      { index: true, element: <Index /> },
      { path: "obra", element: <Only roles={S}><Workspace /></Only> },
      { path: "sesiones", element: <Only roles={S}><SessionsPage /></Only> },
      { path: "sesiones/:id", element: <Only roles={S}><SessionDetailPage /></Only> },
      { path: "versiones", element: <Only roles={S}><VersionsPage /></Only> },
      { path: "revisiones/:id", element: <Only roles={S}><ReviewView /></Only> },
      { path: "analisis", element: <Only roles={S}><AnalysesPage /></Only> },
      { path: "analisis/:id", element: <Only roles={S}><AnalysisDetail /></Only> },
      { path: "ajustes", element: <Only roles={S}><StudentSettings /></Only> },
      { path: "profesor", element: <Only roles={["profesor"]}><TeacherHome /></Only> },
      { path: "profesor/grupos", element: <Only roles={["profesor"]}><TeacherGroups /></Only> },
      { path: "profesor/revision/:id", element: <Only roles={["profesor"]}><TeacherReview /></Only> },
      { path: "profesor/clases", element: <Only roles={T}><TeacherClasses /></Only> },
      { path: "profesor/clases/:id", element: <Only roles={T}><ClassDetailPage /></Only> },
      { path: "coordinacion", element: <Only roles={C}><CoordHome /></Only> },
      { path: "coordinacion/generaciones", element: <Only roles={["coordinacion"]}><CohortsPage /></Only> },
      { path: "coordinacion/personas", element: <Only roles={["coordinacion"]}><PeoplePage /></Only> },
      { path: "coordinacion/solicitudes", element: <Only roles={["coordinacion"]}><LeadsPage /></Only> },
      { path: "coordinacion/ajustes", element: <Only roles={["coordinacion"]}><OrgSettings /></Only> },
      {
        path: "*",
        element: <Empty title="Esta página no existe" action={<Link to="/"><Button>Volver al inicio</Button></Link>} />,
      },
    ],
  },
]);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AuthProvider>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </AuthProvider>
  </StrictMode>,
);
